import { AlertDialog } from '@base-ui/react/alert-dialog'
import { ladderAccess, levelSomewhere, useAccess, useChartEditor } from '@church/data'
import { atLeast } from '@church/permissions'
import { faFloppyDisk, faXmark } from '@church/ui-icons'
import { createFileRoute, redirect, useBlocker, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { z } from 'zod'
import { DesktopChartEditor } from '../components/services/chart-editor/DesktopChartEditor.tsx'
import { MobileChartEditor } from '../components/services/chart-editor/MobileChartEditor.tsx'
import { Button, EmptyState, ErrorNote, PageHeader, Spinner } from '../components/ui.tsx'
import { useBreadcrumbs } from '../lib/breadcrumbs.ts'
import { useWideScreen } from '../lib/media.ts'

/**
 * Editing an arrangement's chart: a page of its own, so the chart gets the
 * whole screen. The song comes in the address (`?song=`) — the arrangement is
 * read through it, as everywhere else.
 *
 * Two editors over one text and one layout: on a computer the plain text
 * with a live preview; on a phone a line-at-a-time editor made for fingers
 * (each in its own component). Save writes the chart and its layout together;
 * Ctrl/Cmd+S saves too. Leaving with unsaved changes asks first, in the app
 * and when the tab closes.
 */
export const Route = createFileRoute('/_authed/_shell/services/arrangements/$arrangementId/chart')({
  validateSearch: z.object({ song: z.uuid() }),
  beforeLoad: ({ context }) => {
    // Changing a chart is Songs Editor (docs/13-ACCESS.md).
    if (!atLeast(levelSomewhere(ladderAccess(context.me), 'songs'), 'editor'))
      throw redirect({ to: '/services/songs' })
  },
  component: ChartEditPage,
})

function ChartEditPage() {
  const { arrangementId } = Route.useParams()
  const { song: songId } = Route.useSearch()
  const navigate = useNavigate()
  const access = useAccess()
  const chart = useChartEditor(songId, arrangementId)
  const wide = useWideScreen()
  // Set just before leaving on purpose (after a save, or Cancel), so the guard stands aside.
  const leaving = useRef(false)
  const blocker = useBlocker({
    shouldBlockFn: () => chart.dirty && !leaving.current,
    enableBeforeUnload: () => chart.dirty,
    withResolver: true,
  })

  const songHref = `/services/songs/${songId}`
  const arrangementHref = `/services/songs/${songId}/arrangements/${arrangementId}`
  useBreadcrumbs([
    { label: 'Songs', href: '/services/songs' },
    { label: 'Song', href: songHref },
    { label: chart.arrangement?.name ?? 'Arrangement', href: arrangementHref },
    { label: 'Chart' },
  ])
  const back = () => {
    leaving.current = true
    void navigate({
      to: '/services/songs/$songId/arrangements/$arrangementId',
      params: { songId, arrangementId },
    })
  }

  // Ctrl/Cmd+S saves (and stays on the page), wherever the focus is.
  const save = () => {
    if (chart.dirty && !chart.saving) chart.save()
  }
  const saveRef = useRef(save)
  useEffect(() => {
    saveRef.current = save
  })
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault()
        saveRef.current()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  if (chart.isPending) return <Spinner />
  if (!chart.song || !chart.arrangement) return <EmptyState title="That arrangement is gone" />
  const writable = access.hasSomewhere('songs', 'editor')

  return (
    <div className="space-y-4">
      <PageHeader
        title={`${chart.song.title} · ${chart.arrangement.name} · Chart`}
        facts={`${chart.song.title} · ${chart.arrangement.name}${
          chart.arrangement.key ? ` · Original key ${chart.arrangement.key}` : ''
        }`}
        action={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              icon={faXmark}
              onClick={() => {
                chart.discard()
                back()
              }}
            >
              Cancel
            </Button>
            <Button
              icon={faFloppyDisk}
              disabled={!writable || !chart.dirty || chart.saving}
              onClick={() => chart.save({ onSuccess: back })}
            >
              {chart.saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        }
      />
      <ErrorNote error={chart.saveError} />
      {wide ? <DesktopChartEditor chart={chart} /> : <MobileChartEditor chart={chart} />}

      <AlertDialog.Root
        open={blocker.status === 'blocked'}
        onOpenChange={(open) => {
          if (!open && blocker.status === 'blocked') blocker.reset()
        }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Backdrop className="fixed inset-0 bg-black/40" />
          <AlertDialog.Viewport className="fixed inset-0 flex items-center justify-center p-4">
            <AlertDialog.Popup className="w-full max-w-md space-y-3 rounded-xl border border-border-subtle bg-surface-overlay p-6 shadow-xl">
              <AlertDialog.Title className="text-base font-semibold text-content-default">
                Leave without saving?
              </AlertDialog.Title>
              <AlertDialog.Description className="text-sm text-content-muted">
                Your changes to the chart will be lost.
              </AlertDialog.Description>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" onClick={() => blocker.reset?.()}>
                  Keep editing
                </Button>
                <Button variant="danger" onClick={() => blocker.proceed?.()}>
                  Leave
                </Button>
              </div>
            </AlertDialog.Popup>
          </AlertDialog.Viewport>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </div>
  )
}
